// 채팅 **별도 창** 규약(설계서 §9.6.6) — 세션 창(host) ↔ 채팅 창(win) 사이에 오가는 메시지.
//
// 🔴 진실원은 **세션 창 하나**다. 채팅 채널(DataChannel)·대화 목록·오류는 세션 창의 `useChat` 에 있고,
//    채팅 창은 그 상태를 받아 그리고 사람의 동작(보내기·저장·고정으로 돌리기)을 되돌려 보낼 뿐이다.
//    두 곳이 각자 목록을 들면 어긋난다(한쪽만 보낸 글·한쪽만 받은 글).
//
// 전달 수단은 둘이지만 **메시지 모양은 하나**다(`popupLink.ts`):
//   - 통합앱: Rust 가 만든 창 + Tauri 이벤트(`emitTo`)
//   - 브라우저: `window.open` + `postMessage`
// 그래서 그림은 **data: URL** 로 보낸다 — `blob:` URL 은 만든 창(웹뷰)에서만 유효하다(앱의 창은 서로 다른 웹뷰다).

import type { SaveNotice } from '../download';
import { newPopupKey } from '../popup/popupKey';
import type { PopupSpec } from '../popup/windowLink';
import type { ChatEntry } from './channel';

/**
 * 채팅 창 종류 값. 🔴 src-tauri `popup_window.rs` 의 `CHAT` 과 같아야 한다
 * (이벤트 이름 — Rust 가 `bye` 를 `evtToHost` 로 보낸다 · 명령 이름 · 전역 이름 · 크기).
 */
export const CHAT_WINDOW_SPEC: PopupSpec = {
  page: 'chat.html',
  tag: 'rsChat',
  browserPrefix: 'rs-chat-',
  browserFeatures: 'popup,width=400,height=600',
  evtToHost: 'rs-chatwin-to-host',
  evtToWin: 'rs-chatwin-to-win',
  openCommand: 'open_chat_window',
  closeCommand: 'close_chat_window',
  initGlobal: '__RS_CHAT__',
};
/** 초안을 세션 창에 알리는 간격(ms) — 글자마다 보내지 않는다. */
export const DRAFT_SYNC_MS = 300;

/** 대화 한 건(전송용) — 그림은 따로(`image`) 보낸다. 목록을 다시 보낼 때마다 그림까지 실으면 무겁다. */
export interface WireEntry {
  id: string;
  from: 'me' | 'them';
  kind: 'text' | 'image';
  text?: string;
  at: number;
  delivered?: boolean;
  error?: string;
}

/** 채팅 창이 그릴 상태(대화 목록 제외). */
export interface ChatViewState {
  title: string;
  available: boolean;
  canSend: boolean;
  blockedReason?: string;
  canSendImage: boolean;
  /** 그림 상한(바이트) — 채팅 창이 첨부할 때 미리 거른다. */
  maxImage: number;
  error?: string;
  notice?: SaveNotice;
  /** 창을 열 때의 초안(세션 창 패널에서 쓰던 글을 이어 쓴다). */
  draft: string;
}

export type HostToWin =
  | { t: 'state'; s: ChatViewState }
  | { t: 'entries'; entries: WireEntry[] }
  | { t: 'image'; id: string; dataUrl: string }
  /** 세션이 끝났다 — 창은 곧 닫힌다. */
  | { t: 'end' };

export type WinToHost =
  /** 창이 떴다 — 전체 상태를 다시 보내 달라(그 전에 보낸 것은 리스너가 없어 사라졌다). */
  | { t: 'hello' }
  /** 보내기 — 그림은 base64(PNG). 첨부 확인은 채팅 창에서 끝났다. */
  | { t: 'send'; text: string; png?: string }
  | { t: 'download' }
  | { t: 'draft'; text: string }
  /** 옆에 고정으로 돌린다(쓰던 초안을 넘긴다). */
  | { t: 'dock'; draft: string }
  /** 채팅을 닫는다(창의 ✕). */
  | { t: 'close' }
  | { t: 'clearError' }
  | { t: 'clearNotice' }
  /** 창이 사라졌다 — 앱은 Rust 가, 브라우저는 세션 창의 감시가 만든다(창 스스로는 보내지 않는다). */
  | { t: 'bye' };

const WIN_TO_HOST = new Set(['hello', 'send', 'download', 'draft', 'dock', 'close', 'clearError', 'clearNotice', 'bye']);
const HOST_TO_WIN = new Set(['state', 'entries', 'image', 'end']);

/** 받은 값이 채팅 창 메시지인가(모양만 — 내용 검사는 세션 창의 채널이 한다). */
export function isWinToHost(v: unknown): v is WinToHost {
  return !!v && typeof v === 'object' && WIN_TO_HOST.has((v as { t?: unknown }).t as string);
}

export function isHostToWin(v: unknown): v is HostToWin {
  return !!v && typeof v === 'object' && HOST_TO_WIN.has((v as { t?: unknown }).t as string);
}

export function toWire(e: ChatEntry): WireEntry {
  return { id: e.id, from: e.from, kind: e.kind, text: e.text, at: e.at, delivered: e.delivered, error: e.error };
}

/** 세션 키 — 창 label 에 들어가므로 **영숫자·하이픈만**(Rust `valid_key` 와 같은 규칙). */
export function newChatKey(): string {
  return newPopupKey('c');
}

/** PNG 바이트 ↔ base64(메시지는 JSON 으로 가므로 바이트 배열을 그대로 실을 수 없다). */
export function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  const CHUNK = 0x8000; // 한 번에 너무 많은 인자를 넘기면 스택이 넘친다
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

export function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    out[i] = bin.charCodeAt(i);
  }
  return out;
}

/** 세션 창의 `blob:` URL → data: URL(채팅 창으로 보낼 수 있는 모양). */
export async function blobUrlToDataUrl(url: string): Promise<string> {
  const blob = await (await fetch(url)).blob();
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}
